import type { ApiClient } from '../api/client';
import { META, readMeta, writeMeta } from '../db/schema';
import type { AppDb } from '../db/schema';
import { ReportSummary } from '../wire';
import type { ReportSummary as ReportSummaryType } from '../wire';

/**
 * **Today so far** — what the counter has taken since the tenant's midnight,
 * as the ledger folds it.
 *
 * It is `reports.ts`'s argument, one day wide. The number is `GET
 * /reports/summary` with `date_from = date_to = today`, because the fold that
 * decides which tender a split sale landed on, what a refund took back and
 * which `timestamptz` belongs to which day is SQL on the server, and a device
 * adding up the settlements it happens to remember would be a second answer to
 * the one question the owner asks at the counter.
 *
 * ## Why this is not a seventh read inside `Reports`
 *
 * `Reports` stores its six answers as **one** snapshot on purpose — a screen
 * that can state its own age only if everything on it is the same age — and it
 * is refreshed when somebody opens Reports. This one is refreshed after every
 * sale. Folding it in would either re-fetch a year of top clients each time the
 * till closes, or make the batch a mixture of ages, which is the thing that
 * class exists to prevent. Same shape, same META helpers, separate snapshot.
 *
 * ## Keyed to the day
 *
 * The snapshot carries the day it answers for, and `snapshot(day)` returns
 * nothing for any other day. An iPad switched on at nine the next morning,
 * offline, must not open on yesterday's $640 under the words "today" — an empty
 * line is the honest thing there, and the refresh fills it the moment there is
 * a network.
 */

export interface TodayTakingsSnapshot {
  /** The tenant-zone day this answers for, `YYYY-MM-DD`. */
  day: string;
  /** When the server answered. The staleness clause is computed from this. */
  fetchedAt: string;
  summary: ReportSummaryType;
}

export interface TodayTakingsOptions {
  db: AppDb;
  api: ApiClient;
  onChange: () => void;
  now?: () => number;
}

export class TodayTakings {
  private readonly db: AppDb;
  private readonly api: ApiClient;
  private readonly onChange: () => void;
  private readonly now: () => number;
  /** One refresh per day at a time; a sale landing mid-refresh joins it. */
  private inFlight: { day: string; run: Promise<void> } | null = null;

  constructor(options: TodayTakingsOptions) {
    this.db = options.db;
    this.api = options.api;
    this.onChange = options.onChange;
    this.now = options.now ?? Date.now;
  }

  /**
   * The last answer for `day`, or `null` — including when the last answer was
   * for another day.
   *
   * **Re-parsed on the way out**, through the same `ReportSummary` the fetch
   * used. A snapshot is stored as the object the schema produced *on the day it
   * was fetched*, and IndexedDB outlives bundles: an iPad that cached this
   * morning's answer and then updated to a bundle that reads
   * `certificate_by_tender` (0070) would otherwise hand the screen an object
   * without it — `undefined['cash']`, a blank Today. Found live on the local
   * rig, not imagined. Parsing again applies every `.default` an additive field
   * carries, so the next field is safe the same way; an answer that no longer
   * parses at all is `null`, which the line already renders honestly.
   */
  async snapshot(day: string): Promise<TodayTakingsSnapshot | null> {
    const stored = await readMeta<TodayTakingsSnapshot>(this.db, META.todayTakings);
    if (stored === undefined || stored.day !== day) return null;
    const summary = ReportSummary.safeParse(stored.summary);
    return summary.success ? { ...stored, summary: summary.data } : null;
  }

  async refresh(day: string): Promise<void> {
    if (this.inFlight !== null && this.inFlight.day === day) return this.inFlight.run;
    const run = this.fetch(day).finally(() => {
      if (this.inFlight?.run === run) this.inFlight = null;
    });
    this.inFlight = { day, run };
    return run;
  }

  private async fetch(day: string): Promise<void> {
    const summary = await this.api.request({
      method: 'GET',
      path: '/reports/summary',
      query: { date_from: day, date_to: day },
      schema: ReportSummary,
    });
    const snapshot: TodayTakingsSnapshot = {
      day,
      fetchedAt: new Date(this.now()).toISOString(),
      summary,
    };
    await writeMeta(this.db, META.todayTakings, snapshot);
    this.onChange();
  }
}
